import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { kebabCase } from 'lodash'
import Markdown from 'markdown-to-jsx'
import {
  type ChangeEvent,
  type ChangeEventHandler,
  type ComponentProps,
  Fragment,
  useId,
  useState,
} from 'react'
import { LuPaperclip } from 'react-icons/lu'

import { absoluteFill } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { fieldStyles } from './styles/fieldStyles'

type Props = {
  data: Queries.FormAttachmentFieldFragment | null

  sectionName?: string | null
}

export const FormAttachmentField = ({ data, sectionName }: Props) => {
  const name = `ATTACHMENT--${sectionName ? kebabCase(sectionName) + '-' : ''}${data?.label ? kebabCase(data?.label) : ''}`
  const uniqueId = useId()
  const getFiletypes = () => {
    const options = JSON.parse(
      data?.fileTypes as unknown as string
    ) as string[]
    return options
      .map(option => {
        switch (option) {
          case 'Images':
            return 'image/*'
          case 'Documents':
            '.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf'
        }
      })
      .join(',')
  }

  const MAX_TOTAL_BYTES = 18 * 1024 * 1024 // 18 MiB
  const [label, setLabel] = useState<string>(
    data?.label || 'No files selected'
  )
  const [error, setError] = useState<string | null>(null)
  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    const input = e.currentTarget
    const files = Array.from(input.files || [])
    setError(null)

    if (!files || files.length === 0) {
      setLabel(data?.label || 'No files selected')
      return
    }
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0)
    const mb = (totalBytes / (1024 * 1024)).toFixed(1)
    if (totalBytes > MAX_TOTAL_BYTES) {
      setError(
        `Attachments are ${mb}MB total. Please keep attachments under ${(MAX_TOTAL_BYTES / (1024 * 1024)).toFixed(0)}MB.`
      )
      setLabel(data?.label || 'No files selected')

      // Clear the file input selection
      input.value = ''
      return
    }
    const sizeString = `<span> ${mb}MB</span>`
    if (files.length > 1) {
      setLabel(`${files.length} files selected ${sizeString}`)
    } else {
      setLabel(`${files[0].name} ${sizeString}`)
    }
  }
  const styles = {
    container: css`
      position: relative;
      display: flex;
      cursor: pointer;
      justify-content: center;
      align-items: center;

      border-radius: 0.25em;
      background-color: ${colors.gray95};
      border: 1px solid ${colors.gray75};
      color: ${colors.gray45};
      min-height: 1.75em;
      transition: all 300ms ease;
      @media (hover: hover) {
        &:hover {
          background-color: ${colors.red};
          color: #fff;
          border-color: ${colors.redDark};
        }
      }
    `,
    icon: css`
      font-size: 90%;
    `,
    label: css`
      cursor: pointer;
      display: flex;
      gap: 0.25em;
      justify-content: center;
      align-items: center;
      span span {
        opacity: 0.5;
        font-size: 0.85em;
      }
    `,
    input: css`
      ${absoluteFill}
      opacity: 0;
      cursor: pointer;
    `,
    error: css`
      display: block;
      margin-top: 0.5em;
      font-size: 0.85em;
      color: ${colors.red};
      text-align: center;
      width: 100%;
    `,
    maxSize: css`
      display: block;
      margin-top: 0.5em;
      font-size: 0.85em;
      text-align: center;
      width: 100%;
      color: ${colors.gray65};
    `,
  }
  return (
    <div css={fieldStyles.container('Full')}>
      <div css={styles.container}>
        <label
          css={styles.label}
          htmlFor={name + uniqueId}
        >
          <LuPaperclip css={styles.icon} />
          <Markdown
            options={{
              wrapper: 'span',
              forceInline: true,
              forceWrapper: true,
            }}
            css={styles.label}
          >
            {label}
          </Markdown>
        </label>
        <input
          css={styles.input}
          id={name + uniqueId}
          name={name}
          accept={getFiletypes()}
          multiple={data?.allowMultipleFiles || false}
          type="file"
          onChange={handleChange}
        />
      </div>
      {error ? (
        <Markdown
          options={{
            wrapper: 'span',
            forceBlock: true,
            forceWrapper: true,
          }}
          css={styles.error}
        >
          {error}
        </Markdown>
      ) : (
        <span css={styles.maxSize}>
          Maximum attachment size:{' '}
          {(MAX_TOTAL_BYTES / (1024 * 1024)).toFixed(0)}MB
        </span>
      )}
    </div>
  )
}

export const FormAttachmentFieldFragment = graphql`
  fragment FormAttachmentField on DatoCmsFormAttachmentField {
    __typename
    id: originalId
    label
    allowMultipleFiles
    fileTypes
  }
`
